import { Spacer, Spinner, TabsBarLink } from '@porsche-code/kado-ui';
import {
  PInlineNotification,
  PTabsBar,
} from '@porsche-design-system/components-react';
import {
  createFileRoute,
  Outlet,
  useChildMatches,
} from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { usePersonContext } from '@/context';
import { usePersonInfoQuery } from '@/graphql';

import { apps } from './-helpers';
import { usePersonIdParam } from './-hooks';

export const Route = createFileRoute('/admin/person-info/$personId')({
  component: PersonInfo,
});

function PersonInfo() {
  const { t } = useTranslation(NAMESPACES);
  const { state: personState } = usePersonContext();
  const dateFormat = personState.settings?.dateFormat || 'YYYY/MM/DD';

  const personId = usePersonIdParam()!;

  const { data: person, isPending } = usePersonInfoQuery(
    { id: +personId },
    {
      select: (data) => data.person,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );

  const availableApps = apps(person?.ability.dashboard ?? []);

  const tabs: {
    routeId:
      | '/admin/person-info/$personId/account'
      | '/admin/person-info/$personId/settings'
      | '/admin/person-info/$personId/consents'
      | '/admin/person-info/$personId/groups'
      | '/admin/person-info/$personId/organizations'
      | '/admin/person-info/$personId/$app';
    label: string;
    params?: { app: string };
  }[] = [
    {
      routeId: '/admin/person-info/$personId/account',
      label: t('common:account'),
    },
    {
      routeId: '/admin/person-info/$personId/settings',
      label: t('common:settings'),
    },
    {
      routeId: '/admin/person-info/$personId/consents',
      label: t('admin.personInfo.consents'),
    },
    {
      routeId: '/admin/person-info/$personId/groups',
      label: t('common:groups'),
    },
    {
      routeId: '/admin/person-info/$personId/organizations',
      label: t('common:organizations'),
    },
    ...(availableApps.includes('FieldForceManagement')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.ffm'),
            params: { app: 'field-force-management' },
          },
        ]
      : []),

    ...(availableApps.includes('Greenland')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.greenland'),
            params: { app: 'greenland' },
          },
        ]
      : []),

    ...(availableApps.includes('PorscheStrategy2030')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.porscheStrategy2030'),
            params: { app: 'porsche-strategy-2030' },
          },
        ]
      : []),

    ...(availableApps.includes('NetworkPlanning')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.networkPlanning'),
            params: { app: 'network-planning' },
          },
        ]
      : []),

    ...(availableApps.includes('RetailDesign')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.retailDesign'),
            params: { app: 'retail-design' },
          },
        ]
      : []),

    ...(availableApps.includes('Survey')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.survey'),
            params: { app: 'survey' },
          },
        ]
      : []),

    ...(availableApps.includes('MultisensoryExperience')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.multisensoryExperience'),
            params: { app: 'multisensory-experience' },
          },
        ]
      : []),

    ...(availableApps.includes('PcRoadmap')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.pcRoadmap'),
            params: { app: 'pc-roadmap' },
          },
        ]
      : []),

    ...(availableApps.includes('Apex')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.apex'),
            params: { app: 'apex' },
          },
        ]
      : []),

    ...(availableApps.includes('PorscheImportAgreements')
      ? [
          {
            routeId: '/admin/person-info/$personId/$app' as const,
            label: t('common:app.porscheImportAgreements'),
            params: { app: 'porsche-import-agreements' },
          },
        ]
      : []),
  ];
  const childMatches = useChildMatches();
  const activeTabIndex = tabs.findIndex(
    (tab) =>
      tab.routeId === childMatches[0]?.routeId &&
      (!tab.params ||
        !('app' in childMatches[0].params) ||
        tab.params.app === childMatches[0].params.app),
  );

  return (
    <>
      <Spacer mb="md" />
      {isPending && personId && !person && <Spinner size="xl" />}

      {person && !isPending && (
        <>
          {person.deletedAt && (
            <>
              <PInlineNotification
                heading={t(
                  'admin.personInfo.notification.deletedPersonHeadline',
                )}
                description={t(
                  'admin.personInfo.notification.deletedPersonDescription',
                  {
                    name: person.name,
                    date: dayjs(person.deletedAt).format(dateFormat),
                  },
                )}
                state="warning"
                dismissButton={false}
              />
              <Spacer mb="md" />
            </>
          )}

          {!person.roles.length && (
            <>
              <PInlineNotification
                heading={t(
                  'admin.personInfo.notification.personWithoutRoleHeadline',
                )}
                description={t(
                  'admin.personInfo.notification.personWithoutRoleDescription',
                  { name: person.name },
                )}
                state="info"
                dismissButton={false}
              />
              <Spacer mb="md" />
            </>
          )}

          <PTabsBar size="small" compact activeTabIndex={activeTabIndex}>
            {tabs.map((tab) => (
              <TabsBarLink
                key={`${tab.routeId?.replace(
                  '$app',
                  tab.params?.app.toLowerCase().replace(' ', '-') ?? '$app',
                )}`}
                to={tab.routeId}
                params={{ personId, ...tab.params }}
              >
                {tab.label}
              </TabsBarLink>
            ))}
          </PTabsBar>

          <Spacer mb="md" />

          <Outlet />
        </>
      )}
    </>
  );
}
